import { ITestTrack } from 'interfaces'
import { getPaperCount, getPaperRequestCount } from 'gql/query'

type PaperRequestNameInput = {
  test_track_id: string
  paper_template_id?: string
}

type ExistingPaperRequest = {
  test_track_id?: string
}

export const getSetLetter = (setIndex: number): string => {
  if (setIndex < 26) {
    return String.fromCharCode(65 + setIndex)
  }
  return `Z${setIndex - 25}`
}

const startOfMonthIso = (date: Date): string => {
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString()
}

const uniqueNameSuffix = (extra = 0): string => {
  return `${String(Date.now() + extra).slice(-6)}`
}

export const buildTrackBasedName = ({
  testTrackName,
  productType,
  setIndex,
  date = new Date(),
  uniqueSuffix,
}: {
  testTrackName?: string
  productType?: string
  setIndex: number
  date?: Date
  uniqueSuffix?: string
}): string => {
  const year = date.getFullYear()
  const monthName = date.toLocaleString('default', { month: 'short' })
  const typePart = (productType || '').trim().toUpperCase()
  const typeSegment = typePart ? `-${typePart}` : ''
  const setLetter = getSetLetter(Math.max(0, setIndex))
  const suffix = uniqueSuffix || uniqueNameSuffix()

  return `${testTrackName || 'Unknown Track'}${typeSegment}-Set ${setLetter}-${monthName}_${year}_${suffix}`
}

export const buildPaperRequestName = (
  paperRequest: PaperRequestNameInput,
  testTracks: ITestTrack[],
  existingRequestsForTrack: ExistingPaperRequest[],
  batchCountByTrack: Map<string, number>,
  productType: string,
  date: Date = new Date()
): string => {
  const testTrackId = paperRequest.test_track_id
  const testTrackName = testTracks.find(track => track?.id === testTrackId)?.name ?? 'Unknown Track'
  const existingCountForTrack = existingRequestsForTrack.filter(request => request.test_track_id === testTrackId).length
  const batchOffset = batchCountByTrack.get(testTrackId) ?? 0
  batchCountByTrack.set(testTrackId, batchOffset + 1)

  return buildTrackBasedName({
    testTrackName,
    productType,
    setIndex: existingCountForTrack + batchOffset,
    date,
    uniqueSuffix: uniqueNameSuffix(batchOffset),
  })
}

export const buildPaperRequestNames = (
  paperRequests: PaperRequestNameInput[],
  testTracks: ITestTrack[],
  existingRequestsForTrack: ExistingPaperRequest[],
  productType: string,
  date: Date = new Date()
): string[] => {
  const batchCountByTrack = new Map<string, number>()

  return paperRequests.map(paperRequest =>
    buildPaperRequestName(paperRequest, testTracks, existingRequestsForTrack, batchCountByTrack, productType, date)
  )
}

const countForTrackThisMonth = async (
  testTrackId: string | undefined,
  resource: 'papers' | 'paper_requests',
  date: Date
): Promise<number> => {
  if (!testTrackId) return 0

  const where = {
    test_track_id: { _eq: testTrackId },
    created_at: { _gte: startOfMonthIso(date) },
  }

  try {
    if (resource === 'papers') {
      const data: any = await getPaperCount(where)
      return data?.papers_aggregate?.aggregate?.count ?? 0
    }
    const data: any = await getPaperRequestCount(where)
    return data?.paper_requests_aggregate?.aggregate?.count ?? 0
  } catch (err) {
    console.error('Failed to count existing items for test track', err)
    return 0
  }
}

export const generateTrackBasedPaperName = async ({
  testTrackId,
  testTrackName,
  productType,
  date = new Date(),
}: {
  testTrackId?: string
  testTrackName?: string
  productType?: string
  date?: Date
}): Promise<string> => {
  const setIndex = await countForTrackThisMonth(testTrackId, 'papers', date)
  return buildTrackBasedName({ testTrackName, productType, setIndex, date })
}

export const generateTrackBasedPaperRequestName = async ({
  testTrackId,
  testTrackName,
  productType,
  date = new Date(),
}: {
  testTrackId?: string
  testTrackName?: string
  productType?: string
  date?: Date
}): Promise<string> => {
  const setIndex = await countForTrackThisMonth(testTrackId, 'paper_requests', date)
  return buildTrackBasedName({ testTrackName, productType, setIndex, date })
}

export const getPaperRequestCountsByTrackThisMonth = async (
  testTrackIds: string[],
  date: Date = new Date()
): Promise<ExistingPaperRequest[]> => {
  const uniqueIds = [...new Set(testTrackIds.filter(Boolean))]
  const counts = await Promise.all(uniqueIds.map(id => countForTrackThisMonth(id, 'paper_requests', date)))

  return uniqueIds.flatMap((id, index) => Array.from({ length: counts[index] }, () => ({ test_track_id: id })))
}
